import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Loader2, ShieldOff, Plus, Pencil, Trash2, X, Save, Eye, EyeOff, ArrowLeft, ExternalLink, Instagram, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'clean'],
  ],
};

const CATEGORIES = [
  { key: 'guides', label: 'Build Guides' },
  { key: 'tips', label: 'Setup Tips' },
  { key: 'news', label: 'News' },
  { key: 'reviews', label: 'Reviews' },
];

function slugify(s) {
  return String(s || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

const EMPTY = {
  title: '', slug: '', excerpt: '', body_html: '', category: 'guides',
  cover_image: '', tags: '', author_name: '', published: false,
  seo_title: '', seo_description: '', featured: false,
};

export default function AdminBlog() {
  const { user, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiCategory, setAiCategory] = useState('guides');
  const [aiLoading, setAiLoading] = useState(false);
  const qc = useQueryClient();

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['admin-blog-posts'],
    queryFn: () => base44.entities.BlogPost.list('-updated_date', 200),
    enabled: user?.role === 'admin',
  });

  if (authLoading) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center"><ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p></div>
      </div>
    );
  }

  const openNew = () => { setEditing({}); setForm(EMPTY); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, tags: Array.isArray(p.tags) ? p.tags.join(', ') : '' }); };
  const close = () => setEditing(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // AI draft generation: turn a few keywords into a complete, editable article.
  const openAi = () => { setAiOpen(true); setAiKeywords(''); setAiCategory('guides'); };
  const generateAi = async () => {
    const kw = aiKeywords.trim();
    if (!kw || aiLoading) return;
    setAiLoading(true);
    try {
      const prompt = `You are the content writer for Motion Reality, a UK premium sim racing hardware store and cockpit booking experience venue in Bromborough (near Liverpool). Write a complete, publish-ready long-form blog article based on these keywords: "${kw}".

Category hint: ${aiCategory}. Pick the single best-fitting category from guides, tips, news, reviews (override the hint if it clearly fits another better).

Requirements:
- title: a compelling, SEO-friendly article title (title case).
- slug: a url-friendly slug, lowercase, hyphenated, no stop words.
- excerpt: a 1-2 sentence summary for cards and the meta description.
- body_html: a full HTML article, roughly 600-900 words, using <h2>, <h3>, <p>, <ul>/<li>, <strong>. Do NOT use <h1>. Practical, authoritative, no fluff, no marketing puffery. Mention Motion Reality naturally where genuinely relevant (hardware we sell, sim racing experiences, bookings) but keep the piece informational and useful, not salesy.
- tags: 4-6 relevant lowercase tags.
- author_name: "Motion Reality".
- seo_title: a title tag, max 60 characters.
- seo_description: a meta description, max 155 characters.

Return strict JSON only, no commentary.`;
      const schema = {
        type: 'object',
        properties: {
          title: { type: 'string' },
          slug: { type: 'string' },
          excerpt: { type: 'string' },
          category: { type: 'string', enum: ['guides', 'tips', 'news', 'reviews'] },
          body_html: { type: 'string' },
          tags: { type: 'array', items: { type: 'string' } },
          author_name: { type: 'string' },
          seo_title: { type: 'string' },
          seo_description: { type: 'string' },
        },
      };
      const d = await base44.integrations.Core.InvokeLLM({ prompt, response_json_schema: schema }) || {};
      setForm({
        ...EMPTY,
        title: d.title || '',
        slug: slugify(d.slug || d.title || ''),
        excerpt: d.excerpt || '',
        body_html: d.body_html || '',
        category: ['guides', 'tips', 'news', 'reviews'].includes(d.category) ? d.category : aiCategory,
        tags: Array.isArray(d.tags) ? d.tags.join(', ') : '',
        author_name: d.author_name || 'Motion Reality',
        seo_title: d.seo_title || '',
        seo_description: d.seo_description || '',
        published: false,
        featured: false,
      });
      setEditing({});
      setAiOpen(false);
    } catch (e) {
      toast({ title: 'AI generation failed', description: e.message, variant: 'destructive' });
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.slug.trim()) return;
    setSaving(true);
    try {
      const wasPublished = editing?.published;
      const publishNow = !!form.published;
      const payload = {
        title: form.title.trim(),
        slug: slugify(form.slug),
        excerpt: form.excerpt.trim(),
        body_html: form.body_html,
        category: form.category,
        cover_image: form.cover_image.trim(),
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        author_name: form.author_name.trim(),
        published: publishNow,
        published_date: publishNow ? (editing?.published_date || new Date().toISOString()) : null,
        seo_title: form.seo_title.trim(),
        seo_description: form.seo_description.trim(),
        featured: !!form.featured,
      };
      if (editing?.id) {
        await base44.entities.BlogPost.update(editing.id, payload);
      } else {
        await base44.entities.BlogPost.create(payload);
      }
      qc.invalidateQueries(['admin-blog-posts']);
      close();
      toast({ title: editing?.id ? 'Article updated' : 'Article created' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await base44.entities.BlogPost.delete(deleteTarget.id);
      qc.invalidateQueries(['admin-blog-posts']);
      toast({ title: 'Article deleted' });
      setDeleteTarget(null);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  const togglePublish = async (p) => {
    const published_date = !p.published ? (p.published_date || new Date().toISOString()) : null;
    try {
      await base44.entities.BlogPost.update(p.id, { published: !p.published, published_date });
      qc.invalidateQueries(['admin-blog-posts']);
    } catch (e) {
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">MARKETING &amp; COMMS</p>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Blog &amp; Guides</h1>
            <div className="flex items-center gap-2">
              <button onClick={openAi} className="inline-flex items-center gap-2 border border-primary/40 text-primary hover:bg-primary/10 font-bold text-xs tracking-wider px-4 py-3 transition-colors">
                <Sparkles className="w-4 h-4" /> AI ARTICLE
              </button>
              <button onClick={openNew} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors">
                <Plus className="w-4 h-4" /> NEW ARTICLE
              </button>
            </div>
          </div>
          <p className="text-white/40 text-sm mt-2 max-w-2xl">Long-form guides and tips for SEO. Published posts appear at /blog/&lt;slug&gt; and are included in the sitemap.</p>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20"><p className="text-white/30 text-sm">No articles yet. Click "New Article" to create your first post.</p></div>
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="bg-[#111115] border border-white/5 p-4 flex items-center gap-4">
                <div className="w-16 h-12 bg-[#0d0d0f] overflow-hidden shrink-0">
                  {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-bold text-sm truncate">{p.title}</h3>
                    <span className={`text-[9px] font-bold tracking-widest px-2 py-0.5 ${p.published ? 'bg-primary/15 text-primary' : 'bg-white/5 text-white/40'}`}>
                      {p.published ? 'PUBLISHED' : 'DRAFT'}
                    </span>
                    {p.featured && <span className="text-[9px] font-bold tracking-widest px-2 py-0.5 bg-yellow-500/15 text-yellow-400">FEATURED</span>}
                  </div>
                  <p className="text-white/30 text-xs mt-1">/blog/{p.slug} · {p.category} {p.published_date ? `· ${new Date(p.published_date).toLocaleDateString('en-GB')}` : ''}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => togglePublish(p)} title={p.published ? 'Unpublish' : 'Publish'}
                    className="w-9 h-9 flex items-center justify-center border border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors">
                    {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  {p.published && (
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="View"
                      className="w-9 h-9 flex items-center justify-center border border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => openEdit(p)} title="Edit"
                    className="w-9 h-9 flex items-center justify-center border border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteTarget(p)} title="Delete"
                    className="w-9 h-9 flex items-center justify-center border border-white/10 text-white/50 hover:text-red-400 hover:border-red-400/40 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto">
          <div className="w-full max-w-lg bg-[#111115] border border-white/10 my-8">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h2 className="text-white font-black text-lg uppercase flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> AI Article</h2>
              <button onClick={() => setAiOpen(false)} disabled={aiLoading} className="text-white/40 hover:text-white disabled:opacity-40"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">KEYWORDS / TOPIC</Label>
                <textarea value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)} rows={3}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm p-2 focus:outline-none focus:border-primary/60 resize-none"
                  placeholder="e.g. direct drive vs gear belt, load cell pedals, triple monitor setup for ACC" />
                <p className="text-white/30 text-[10px] mt-1.5">A few words or a topic — the AI writes the whole article. You can edit it before saving.</p>
              </div>
              <div>
                <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">CATEGORY HINT</Label>
                <select value={aiCategory} onChange={(e) => setAiCategory(e.target.value)} className="w-full bg-[#0d0d0f] border border-white/10 text-white h-9 px-2 text-sm focus:outline-none">
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-[#0d0d0f]">{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-white/5">
              <button onClick={() => setAiOpen(false)} disabled={aiLoading} className="text-white/50 hover:text-white text-xs font-bold tracking-wider px-5 py-3 transition-colors disabled:opacity-40">CANCEL</button>
              <button onClick={generateAi} disabled={aiLoading || !aiKeywords.trim()}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-6 py-3 transition-colors disabled:opacity-40">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {aiLoading ? 'GENERATING…' : 'GENERATE DRAFT'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete article"
        description={deleteTarget ? `This will permanently delete "${deleteTarget.title}". This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={remove}
        onClose={() => setDeleteTarget(null)}
      />

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto">
          <div className="w-full max-w-3xl bg-[#111115] border border-white/10 my-8">
            <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#111115] z-10">
              <h2 className="text-white font-black text-lg uppercase">{editing.id ? 'Edit Article' : 'New Article'}</h2>
              <button onClick={close} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">TITLE</Label>
                <Input value={form.title} onChange={(e) => set({ title: e.target.value, slug: editing.id ? form.slug : slugify(e.target.value) })}
                  className="bg-[#0d0d0f] border-white/10 text-white" placeholder="What is Direct Drive? A Sim Racing Beginner's Guide" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SLUG</Label>
                  <Input value={form.slug} onChange={(e) => set({ slug: slugify(e.target.value) })} className="bg-[#0d0d0f] border-white/10 text-white font-mono text-xs" placeholder="what-is-direct-drive" />
                </div>
                <div>
                  <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">CATEGORY</Label>
                  <select value={form.category} onChange={(e) => set({ category: e.target.value })} className="w-full bg-[#0d0d0f] border border-white/10 text-white h-9 px-2 text-sm focus:outline-none">
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-[#0d0d0f]">{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">EXCERPT</Label>
                <textarea value={form.excerpt} onChange={(e) => set({ excerpt: e.target.value })} rows={2}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm p-2 focus:outline-none focus:border-primary/60 resize-none" placeholder="Short summary shown on cards and search results..." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">COVER IMAGE URL</Label>
                  <Input value={form.cover_image} onChange={(e) => set({ cover_image: e.target.value })} className="bg-[#0d0d0f] border-white/10 text-white text-xs" placeholder="https://..." />
                </div>
                <div>
                  <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">AUTHOR</Label>
                  <Input value={form.author_name} onChange={(e) => set({ author_name: e.target.value })} className="bg-[#0d0d0f] border-white/10 text-white" placeholder="Motion Reality" />
                </div>
              </div>
              <div>
                <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">TAGS (comma separated)</Label>
                <Input value={form.tags} onChange={(e) => set({ tags: e.target.value })} className="bg-[#0d0d0f] border-white/10 text-white" placeholder="direct drive, wheel base, beginners" />
              </div>

              <div>
                <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">BODY</Label>
                <div className="email-quill bg-white">
                  <ReactQuill theme="snow" modules={QUILL_MODULES} value={form.body_html} onChange={(v) => set({ body_html: v })} placeholder="Write your article..." />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SEO TITLE (optional)</Label>
                  <Input value={form.seo_title} onChange={(e) => set({ seo_title: e.target.value })} className="bg-[#0d0d0f] border-white/10 text-white text-xs" />
                </div>
                <div>
                  <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SEO DESCRIPTION (optional)</Label>
                  <Input value={form.seo_description} onChange={(e) => set({ seo_description: e.target.value })} className="bg-[#0d0d0f] border-white/10 text-white text-xs" />
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.published} onChange={(e) => set({ published: e.target.checked })} className="w-4 h-4 accent-primary" />
                  <span className="text-white/70 text-sm">Publish (visible on /blog)</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => set({ featured: e.target.checked })} className="w-4 h-4 accent-primary" />
                  <span className="text-white/70 text-sm">Featured</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-5 border-t border-white/5 sticky bottom-0 bg-[#111115]">
              <button onClick={close} className="text-white/50 hover:text-white text-xs font-bold tracking-wider px-5 py-3 transition-colors">CANCEL</button>
              <button onClick={save} disabled={saving || !form.title.trim() || !form.slug.trim()}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-6 py-3 transition-colors disabled:opacity-40">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}